import { create } from 'zustand';

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionTimeout: number | null;
  isInitialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  checkSession: () => void;
  extendSession: () => void;
  restoreSession: () => boolean;
}

const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  sessionTimeout: null,
  isInitialized: false,
  
  login: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock user data - replace with actual API call
      const user: User = {
        id: '1',
        email,
        name: email.split('@')[0],
      };
      
      const sessionTimeout = Date.now() + SESSION_DURATION;
      
      set({ 
        user, 
        isAuthenticated: true, 
        isLoading: false,
        sessionTimeout,
        isInitialized: true
      });

      // Store session data in localStorage
      localStorage.setItem('auth_session', JSON.stringify({
        user,
        sessionTimeout
      }));

      // Set up session check interval
      const checkInterval = setInterval(() => {
        get().checkSession();
      }, 60000); // Check every minute

      // Store interval ID for cleanup
      localStorage.setItem('session_check_interval', checkInterval.toString());
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  logout: () => {
    // Clear session data
    localStorage.removeItem('auth_session');
    localStorage.removeItem('session_check_interval');
    
    // Clear any existing interval
    const intervalId = localStorage.getItem('session_check_interval');
    if (intervalId) {
      clearInterval(parseInt(intervalId));
    }
    
    set({ 
      user: null, 
      isAuthenticated: false,
      sessionTimeout: null,
      isInitialized: true
    });
  },
  
  forgotPassword: async (email: string) => {
    set({ isLoading: true });
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      set({ isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  restoreSession: () => {
    const storedSession = localStorage.getItem('auth_session');
    if (storedSession) {
      try {
        const sessionData = JSON.parse(storedSession);
        const { user, sessionTimeout: storedTimeout } = sessionData;
        
        if (storedTimeout && Date.now() < storedTimeout) {
          // Session is still valid - restore it
          set({ 
            user, 
            isAuthenticated: true,
            sessionTimeout: storedTimeout,
            isInitialized: true
          });
          
          console.log('Session restored successfully for user:', user.email);
          return true;
        } else {
          // Session expired
          console.log('Session expired, clearing stored data');
          get().logout();
          return false;
        }
      } catch (error) {
        console.error('Error parsing stored session:', error);
        get().logout();
        return false;
      }
    } else {
      console.log('No stored session found');
      set({ isInitialized: true });
      return false;
    }
  },

  checkSession: () => {
    const { sessionTimeout } = get();
    
    if (sessionTimeout && Date.now() > sessionTimeout) {
      // Session expired
      console.log('Session timeout reached, logging out');
      get().logout();
      return;
    }

    // Check if session exists in localStorage
    const storedSession = localStorage.getItem('auth_session');
    if (storedSession) {
      try {
        const sessionData = JSON.parse(storedSession);
        const { user, sessionTimeout: storedTimeout } = sessionData;
        
        if (storedTimeout && Date.now() < storedTimeout) {
          // Session is still valid
          set({ 
            user, 
            isAuthenticated: true,
            sessionTimeout: storedTimeout 
          });
        } else {
          // Session expired
          get().logout();
        }
      } catch (error) {
        console.error('Error parsing stored session:', error);
        get().logout();
      }
    }
  },

  extendSession: () => {
    const { user } = get();
    if (user) {
      const newSessionTimeout = Date.now() + SESSION_DURATION;
      
      set({ sessionTimeout: newSessionTimeout });
      
      // Update localStorage
      localStorage.setItem('auth_session', JSON.stringify({
        user,
        sessionTimeout: newSessionTimeout
      }));
      
      console.log('Session extended for user:', user.email);
    }
  },
}));

// Initialize session check on app load
export const initializeAuth = () => {
  const { restoreSession, checkSession } = useAuthStore.getState();
  
  console.log('Initializing authentication system...');
  
  // Try to restore session immediately
  const sessionRestored = restoreSession();
  
  if (sessionRestored) {
    // Set up periodic session checks only if session was restored
    const checkInterval = setInterval(() => {
      checkSession();
    }, 60000); // Check every minute
    
    // Store interval ID
    localStorage.setItem('session_check_interval', checkInterval.toString());
    
    // Clean up interval on page unload
    window.addEventListener('beforeunload', () => {
      clearInterval(checkInterval);
    });
  } else {
    // Set up periodic session checks even if no session was restored
    // This handles cases where user logs in later
    const checkInterval = setInterval(() => {
      checkSession();
    }, 60000); // Check every minute
    
    // Store interval ID
    localStorage.setItem('session_check_interval', checkInterval.toString());
    
    // Clean up interval on page unload
    window.addEventListener('beforeunload', () => {
      clearInterval(checkInterval);
    });
  }
}; 